import React from "react";

/**
 * Formato de texto igual que WhatsApp: *negrita*, _cursiva_, ~tachado~, `código`,
 * ```monoespaciado```, citas "> " y enlaces. No interpreta HTML ni Markdown genérico
 * (así "- - -", "1." o "#" se ven tal cual, como en el teléfono).
 */

const MONO = "SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

const styles = {
  mono: { fontFamily: MONO, fontSize: "0.92em", whiteSpace: "pre-wrap" },
  inlineCode: {
    fontFamily: MONO,
    fontSize: "0.88em",
    padding: "1px 4px",
    borderRadius: 4,
    background: "rgba(127,127,127,0.16)",
  },
  quote: {
    display: "block",
    borderLeft: "3px solid rgba(127,127,127,0.45)",
    paddingLeft: 8,
    margin: "2px 0",
    opacity: 0.78,
  },
  link: { color: "inherit", textDecoration: "underline", wordBreak: "break-all" },
};

const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]'"»])/gi;

const linkify = (text, key) => {
  const out = [];
  let last = 0;
  let match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const href = /^www\./i.test(match[0]) ? `https://${match[0]}` : match[0];
    out.push(
      <a key={`${key}-a${match.index}`} href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>
        {match[0]}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// Marcadores de estilo: el de apertura va al inicio o tras espacio/puntuación y antes de un no-espacio;
// el de cierre va tras un no-espacio y antes de fin, espacio o puntuación (regla de WhatsApp).
const MARKERS = { "*": "strong", _: "em", "~": "del" };
const BOUNDARY = /[\s.,;:!?¡¿()[\]{}"'«»\-*_~]/;

const findClose = (text, marker, from) => {
  for (let i = from; i < text.length; i += 1) {
    if (text[i] === "\n") return -1;
    if (text[i] !== marker) continue;
    const prev = text[i - 1];
    const next = text[i + 1];
    if (i > from && prev && !/\s/.test(prev) && (next === undefined || BOUNDARY.test(next))) return i;
  }
  return -1;
};

export const formatInline = (text, key = "f") => {
  const out = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) out.push(...linkify(buf, `${key}-${out.length}`));
    buf = "";
  };
  while (i < text.length) {
    const ch = text[i];
    if (ch === "`") {
      const end = text.indexOf("`", i + 1);
      if (end > i + 1 && !text.slice(i + 1, end).includes("\n")) {
        flush();
        out.push(<code key={`${key}-c${i}`} style={styles.inlineCode}>{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    const tag = MARKERS[ch];
    if (tag) {
      const prev = text[i - 1];
      const next = text[i + 1];
      const opens = (prev === undefined || BOUNDARY.test(prev)) && next !== undefined && !/\s/.test(next) && next !== ch;
      const end = opens ? findClose(text, ch, i + 1) : -1;
      if (end > i + 1) {
        flush();
        out.push(React.createElement(tag, { key: `${key}-${tag}${i}` }, formatInline(text.slice(i + 1, end), `${key}-${i}`)));
        i = end + 1;
        continue;
      }
    }
    buf += ch;
    i += 1;
  }
  flush();
  return out;
};

/** Agrupa líneas "> " seguidas en una sola cita. */
const formatLines = (text, key, inline) => {
  const lines = text.split("\n");
  const out = [];
  let quote = [];
  const pushQuote = () => {
    if (!quote.length) return;
    const body = quote.join("\n");
    out.push(
      inline
        ? <React.Fragment key={`${key}-q${out.length}`}>{formatInline(body, `${key}-q${out.length}`)}</React.Fragment>
        : <span key={`${key}-q${out.length}`} style={styles.quote}>{formatInline(body, `${key}-q${out.length}`)}</span>
    );
    quote = [];
  };
  lines.forEach((line, idx) => {
    const m = line.match(/^>\s?(.*)$/);
    if (m) {
      quote.push(m[1]);
      return;
    }
    const hadQuote = quote.length > 0;
    pushQuote();
    // La cita es un bloque: el salto que la seguía ya lo pone el bloque.
    if (idx > 0 && !hadQuote) out.push("\n");
    out.push(<React.Fragment key={`${key}-l${idx}`}>{formatInline(line, `${key}-l${idx}`)}</React.Fragment>);
  });
  pushQuote();
  return out;
};

/** Texto completo → nodos React. `inline` (vista previa de la lista) aplana citas. */
export const formatWhatsApp = (value, { inline = false } = {}) => {
  const text = String(value ?? "").replace(/\r\n?/g, "\n");
  const parts = text.split("```");
  // Un ``` sin cierre se deja como texto.
  if (parts.length % 2 === 0) parts[parts.length - 2] += "```" + parts.pop();
  return parts.map((part, idx) =>
    idx % 2 === 1
      ? <span key={`m${idx}`} style={styles.mono}>{part}</span>
      : <React.Fragment key={`t${idx}`}>{formatLines(part, `t${idx}`, inline)}</React.Fragment>
  );
};
